/*
  EKS header search panel (v8.027, ported from ECK's eck-search-panel.js).
  The header's existing search icon becomes a toggle that opens a card under
  the nav instead of going to /search (it stays a real /search link, so it
  still works without JS).
  The Year/Make/Model finder is stamped from a <template> the first time the
  panel opens, so its /products.json fetch only happens when someone uses it.
*/
class EksSearchPanel extends HTMLElement {
  connectedCallback() {
    if (this.panel) return;
    this.toggle = document.querySelector('#eks-nav .nav-actions a[aria-label="Search"]');
    this.panel = this.querySelector('[data-eks-sp-panel]');
    if (!this.toggle || !this.panel) return;
    this.toggle.dataset.labelOpen = 'Search';
    this.toggle.dataset.labelClose = 'Close search';
    this.iconOpen = this.toggle.querySelector('svg');
    this.toggle.insertAdjacentHTML('beforeend',
      '<svg data-eks-sp-icon-close style="display:none" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>');
    this.iconClose = this.toggle.querySelector('[data-eks-sp-icon-close]');
    this.input = this.panel.querySelector('input[type="search"]');
    this.reset = this.panel.querySelector('[data-eks-sp-reset]');
    this.isOpen = false;

    // Hang the panel off the sticky nav so it sits right under the header bar
    // (this section renders after the header, outside the nav).
    const nav = document.getElementById('eks-nav');
    if (nav) nav.append(this.panel);
    this.backdrop = document.createElement('div');
    this.backdrop.className = 'eks-sp-backdrop';
    this.backdrop.hidden = true;
    document.body.append(this.backdrop);

    this.toggle.setAttribute('role', 'button');
    this.toggle.setAttribute('aria-controls', this.panel.id);
    this.toggle.setAttribute('aria-expanded', 'false');

    this.onToggleClick = (e) => { e.preventDefault(); this.isOpen ? this.close(true) : this.open(); };
    this.onToggleKeydown = (e) => { if (e.key === ' ') { e.preventDefault(); this.toggle.click(); } };
    this.onBackdropClick = () => this.close(false);
    this.onDocPointerDown = (e) => {
      if (!this.isOpen || e.target === this.backdrop || this.panel.contains(e.target) || this.toggle.contains(e.target)) return;
      this.close(false);
    };
    this.onDocKeydown = (e) => { if (e.key === 'Escape' && this.isOpen) this.close(true); };
    this.onFocusOut = () => {
      setTimeout(() => {
        const a = document.activeElement;
        if (!this.isOpen || !a || a === document.body || this.panel.contains(a) || this.toggle.contains(a)) return;
        this.close(false);
      });
    };
    this.onInput = () => { if (this.reset) this.reset.hidden = !this.input.value; };
    this.onReset = () => { setTimeout(() => { this.onInput(); this.input.focus(); }); };

    this.toggle.addEventListener('click', this.onToggleClick);
    this.toggle.addEventListener('keydown', this.onToggleKeydown);
    this.backdrop.addEventListener('click', this.onBackdropClick);
    this.panel.addEventListener('focusout', this.onFocusOut);
    if (this.input) this.input.addEventListener('input', this.onInput);
    if (this.input && this.input.form) this.input.form.addEventListener('reset', this.onReset);
    document.addEventListener('pointerdown', this.onDocPointerDown);
    document.addEventListener('keydown', this.onDocKeydown);
  }

  disconnectedCallback() {
    if (!this.panel) return;
    document.removeEventListener('pointerdown', this.onDocPointerDown);
    document.removeEventListener('keydown', this.onDocKeydown);
    this.backdrop.remove();
    this.panel.remove();
    this.panel = null;
  }

  open() {
    this.mountYmm();
    this.isOpen = true;
    this.panel.hidden = false;
    this.backdrop.hidden = false;
    this.setToggleState(true);
    if (this.input) this.input.focus({ preventScroll: true });
  }

  close(returnFocus) {
    if (!this.isOpen) return;
    this.isOpen = false;
    if (this.input && typeof this.input.eksLsClose === 'function') this.input.eksLsClose();
    this.panel.hidden = true;
    this.backdrop.hidden = true;
    this.setToggleState(false);
    if (returnFocus) this.toggle.focus({ preventScroll: true });
  }

  setToggleState(open) {
    this.toggle.setAttribute('aria-expanded', String(open));
    this.toggle.setAttribute('aria-label', open ? this.toggle.dataset.labelClose : this.toggle.dataset.labelOpen);
    if (this.iconOpen) this.iconOpen.style.display = open ? 'none' : '';
    // Inline display (not the hidden attribute): theme CSS sets display on header svgs.
    if (this.iconClose) this.iconClose.style.display = open ? '' : 'none';
  }

  mountYmm() {
    const template = this.panel.querySelector('template[data-eks-sp-ymm-template]');
    const slot = this.panel.querySelector('[data-eks-sp-ymm-slot]');
    if (!template || !slot) return;
    slot.append(template.content.cloneNode(true));
    template.remove();
    const src = this.panel.dataset.ymmScript;
    if (!src || customElements.get('eks-ymm-finder') || document.querySelector('script[src*="/eks-ymm.js"]')) return;
    const script = document.createElement('script');
    script.src = src;
    script.defer = true;
    document.head.append(script);
  }
}
if (!customElements.get('eks-search-panel')) customElements.define('eks-search-panel', EksSearchPanel);
